import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { tr } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { History, RotateCcw, Users, Target } from "lucide-react";
import { motion } from "framer-motion";

export function HistoryView({ scene, onRestore }) {
  const { lang } = useAuth();
  const t = (k) => tr(k, lang);
  const [ah, setAH] = useState([]);
  const [mh, setMH] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/scenes/${scene.scene_id}/history`);
      setAH(r.data.analysis_history || []);
      setMH(r.data.mirroring_history || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [scene.scene_id]);

  const restoreAnalysis = async (i) => {
    const label = lang === "pl" ? "Przywrócić tę wersję Celu i analizy?" : "Restore this analysis version?";
    if (!window.confirm(label)) return;
    setBusy(`a-${i}`);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/history/restore/analysis/${i}`);
      onRestore?.({ analysis: r.data });
      toast.success(lang === "pl" ? "Przywrócono wersję analizy." : "Analysis version restored.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("an.fail"));
    } finally { setBusy(null); }
  };

  const restoreMirror = async (i) => {
    const label = lang === "pl" ? "Przywrócić tę wersję Odzwierciedlenia?" : "Restore this mirroring version?";
    if (!window.confirm(label)) return;
    setBusy(`m-${i}`);
    try {
      const r = await api.post(`/scenes/${scene.scene_id}/history/restore/mirroring/${i}`);
      onRestore?.({ mirroring: r.data });
      toast.success(lang === "pl" ? "Przywrócono Odzwierciedlenie." : "Mirroring restored.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("mir.fail"));
    } finally { setBusy(null); }
  };

  const fmt = (ts) => {
    try { return new Date(ts).toLocaleString(lang === "pl" ? "pl-PL" : "en-US"); } catch { return ts; }
  };

  return (
    <div>
      <div className="mono text-[11px] tracking-[0.4em] text-amber-500 uppercase">
        {lang === "pl" ? "Historia interpretacji" : "Interpretation history"}
      </div>
      <h2 className="serif text-3xl text-stone-100 mt-1">
        {lang === "pl" ? "Zobacz jak zmieniło się Twoje czytanie sceny." : "See how your reading of the scene evolved."}
      </h2>
      <p className="text-stone-400 mt-2 max-w-2xl">
        {lang === "pl"
          ? "Zapisujemy poprzednie wersje Celu, analizy i Odzwierciedlenia. Możesz przywrócić dowolną wersję — aktualna wersja zawsze wskoczy do historii, więc nic nie zginie."
          : "We store previous versions of your Goal, Analysis and Mirroring. Restore any of them — the current version is always pushed into history, so nothing is lost."}
      </p>

      {loading ? (
        <div className="mono text-stone-500 text-sm mt-6">…</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* Analysis history */}
          <div>
            <div className="label-cap flex items-center gap-2"><Target size={12}/> {lang === "pl" ? "Cel i analiza" : "Goal & analysis"}</div>
            {ah.length === 0 ? (
              <div className="paper p-6 mt-3 text-center text-stone-500 text-sm">
                {lang === "pl" ? "Brak poprzednich wersji — jeszcze nie zmienialiśmy analizy." : "No previous versions yet."}
              </div>
            ) : (
              <ul className="mt-3 space-y-3">
                {[...ah].reverse().map((h, revIdx) => {
                  const i = ah.length - 1 - revIdx;
                  const cel = (h.cel_postaci || {}).potrzeba || "—";
                  return (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.02 * revIdx }}
                      className="paper p-4"
                      data-testid={`history-analysis-${i}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="mono text-[10px] uppercase tracking-widest text-stone-500">
                            {fmt(h.ts)} · <span className="text-amber-500/80">{h.source || "analyze"}</span>
                          </div>
                          <div className="serif text-lg text-amber-400 mt-1 truncate">„{cel}"</div>
                          {(h.cel_postaci?.uzasadnienie) && (
                            <div className="text-stone-400 text-sm mt-2 line-clamp-3">{h.cel_postaci.uzasadnienie}</div>
                          )}
                        </div>
                        <button
                          data-testid={`history-analysis-restore-${i}`}
                          disabled={busy === `a-${i}`}
                          onClick={() => restoreAnalysis(i)}
                          className="ghost-btn !py-2 !text-xs shrink-0"
                        >
                          <RotateCcw size={12}/> {lang === "pl" ? "Przywróć" : "Restore"}
                        </button>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Mirroring history */}
          <div>
            <div className="label-cap flex items-center gap-2"><Users size={12}/> {lang === "pl" ? "Odzwierciedlenie w Tobie" : "Mirroring in you"}</div>
            {mh.length === 0 ? (
              <div className="paper p-6 mt-3 text-center text-stone-500 text-sm">
                {lang === "pl" ? "Brak poprzednich wersji." : "No previous versions yet."}
              </div>
            ) : (
              <ul className="mt-3 space-y-3">
                {[...mh].reverse().map((h, revIdx) => {
                  const i = mh.length - 1 - revIdx;
                  return (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.02 * revIdx }}
                      className="paper p-4"
                      data-testid={`history-mirroring-${i}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="mono text-[10px] uppercase tracking-widest text-stone-500">{fmt(h.ts)}</div>
                          {h.osoba && <div className="text-stone-200 text-sm mt-1 line-clamp-2"><b className="text-stone-400 font-normal mono text-[10px] uppercase tracking-widest mr-1">osoba:</b> {h.osoba}</div>}
                          {h.miejsce && <div className="text-stone-200 text-sm mt-1 line-clamp-2"><b className="text-stone-400 font-normal mono text-[10px] uppercase tracking-widest mr-1">miejsce:</b> {h.miejsce}</div>}
                        </div>
                        <button
                          data-testid={`history-mirror-restore-${i}`}
                          disabled={busy === `m-${i}`}
                          onClick={() => restoreMirror(i)}
                          className="ghost-btn !py-2 !text-xs shrink-0"
                        >
                          <RotateCcw size={12}/> {lang === "pl" ? "Przywróć" : "Restore"}
                        </button>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
